/**
 * CRMInsolutiPage — Upload CSV insoluti + storico import.
 * TASK 3 / FASE 3.5, feb 2026.
 */
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, UploadCloud, FileSpreadsheet, Download,
  CheckCircle2, AlertCircle, RefreshCw, Send,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CSV_TEMPLATE = `numero_polizza;codice_fiscale;importo;scadenza;stato;intermediario
POL-001;RSSMRA85M50F839Y;1250.00;2026-03-15;unpaid;INT-001
POL-002;BNCLCU90A01H501X;450.00;2026-03-20;paid;INT-002
POL-003;VRDGNN70M20F839A;980.50;2026-04-05;unpaid;INT-001
`;

export default function CRMInsolutiPage() {
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [lastReport, setLastReport] = useState(null);
  const [queue, setQueue] = useState(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const [imp, q] = await Promise.all([
        axios.get(`${API}/api/admin/crm/insoluti/imports`, { withCredentials: true }),
        axios.get(`${API}/api/admin/crm/reminders/queue`, { withCredentials: true }),
      ]);
      setImports(imp.data.items);
      setQueue(q.data);
    } catch (e) {
      toast.error("Errore caricamento");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await axios.post(`${API}/api/admin/crm/insoluti/upload-csv`, fd, { withCredentials: true });
      setLastReport(data);
      toast.success(`Import OK: ${data.match_ok}/${data.righe_lette} righe matchate`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || "Errore upload");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "insoluti_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const runReminders = async () => {
    try {
      const { data } = await axios.post(`${API}/api/admin/crm/reminders/run-now`, {}, { withCredentials: true });
      toast.success(`Reminder scan: ${data.stats.sent} inviati su ${data.stats.checked} verificati`);
      await load();
    } catch {
      toast.error("Errore esecuzione reminders");
    }
  };

  const rollback = async (id) => {
    if (!window.confirm("Annullare l'import? Le polizze marcate da questo upload torneranno a 'unknown'.")) return;
    try {
      await axios.post(`${API}/api/admin/crm/insoluti/imports/${id}/rollback`, {}, { withCredentials: true });
      toast.success("Import annullato");
      load();
    } catch { toast.error("Errore rollback"); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="admin-insoluti-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl font-semibold text-stone-900 flex items-center gap-2">
          <FileSpreadsheet className="w-6 h-6 text-[#163321]" />Insoluti & Scadenziario
        </h1>
        <div className="flex gap-2">
          <button onClick={downloadTemplate}
            className="inline-flex items-center gap-2 text-sm border border-stone-300 px-3 py-2 rounded-lg hover:bg-stone-50"
            data-testid="btn-download-template">
            <Download className="w-4 h-4" />Template CSV
          </button>
          <button onClick={runReminders}
            className="inline-flex items-center gap-2 text-sm border border-[#163321] text-[#163321] px-3 py-2 rounded-lg hover:bg-[#163321] hover:text-white transition"
            data-testid="btn-run-reminders">
            <Send className="w-4 h-4" />Scansiona reminder ora
          </button>
        </div>
      </div>

      {/* Coda reminders */}
      {queue && (
        <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 mb-6" data-testid="reminders-queue">
          <h3 className="font-semibold text-sky-900 mb-2 flex items-center gap-2"><Send className="w-4 h-4" />Promemoria in coda</h3>
          <div className="grid grid-cols-3 gap-3 text-sm">
            {Object.entries(queue).map(([key, info]) => (
              <div key={key} className="bg-white border border-sky-100 rounded p-2 text-center" data-testid={`queue-${key}`}>
                <div className="text-xs text-stone-500">{key} ({info.date})</div>
                <div className="text-2xl font-bold text-sky-900">{info.count}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload area */}
      <div className="bg-white border-2 border-dashed border-stone-300 rounded-xl p-6 mb-6 text-center" data-testid="upload-area">
        <UploadCloud className="w-10 h-10 text-stone-400 mx-auto mb-3" />
        <p className="font-medium text-stone-800 mb-2">Carica CSV insoluti giornaliero</p>
        <p className="text-xs text-stone-500 mb-4">
          Formati: .csv (.xlsx in arrivo). Max 5MB. Colonne accettate: numero_polizza, codice_fiscale, importo, scadenza, stato, intermediario.
        </p>
        <input ref={fileInputRef} type="file" accept=".csv,.txt" onChange={handleFile} className="hidden" data-testid="file-input" />
        <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
          className="inline-flex items-center gap-2 bg-[#163321] hover:bg-[#0f2517] text-white px-4 py-2 rounded-lg disabled:opacity-60"
          data-testid="btn-upload">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
          Seleziona file
        </button>
      </div>

      {/* Report ultimo upload */}
      {lastReport && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6" data-testid="last-report">
          <h3 className="font-semibold text-emerald-900 mb-2 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />Ultimo import
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div><div className="text-xs text-emerald-700">Righe lette</div><div className="text-xl font-bold">{lastReport.righe_lette}</div></div>
            <div><div className="text-xs text-emerald-700">Match OK</div><div className="text-xl font-bold">{lastReport.match_ok}</div></div>
            <div><div className="text-xs text-rose-700">Insolute</div><div className="text-xl font-bold text-rose-900">{lastReport.polizze_marcate_unpaid}</div></div>
            <div><div className="text-xs text-stone-700">Non matchate</div><div className="text-xl font-bold text-stone-900">{lastReport.match_failed}</div></div>
          </div>
          {lastReport.righe_non_matchate?.length > 0 && (
            <details className="mt-3">
              <summary className="text-xs text-stone-700 cursor-pointer">Vedi righe non matchate ({lastReport.righe_non_matchate.length})</summary>
              <ul className="text-xs text-stone-600 mt-2 ml-4 list-disc">
                {lastReport.righe_non_matchate.slice(0, 10).map((r, i) =>
                  <li key={i}>Riga {r.riga}: polizza={r.numero_polizza || "—"} cf={r.cf || "—"}</li>
                )}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* Storico import */}
      <h2 className="text-lg font-semibold text-stone-900 mb-3">Storico import</h2>
      {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-12" /> :
       imports.length === 0 ? (
        <div className="text-center py-8 bg-stone-50 border border-dashed rounded">
          <p className="text-stone-600 text-sm">Nessun import ancora.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="imports-history">
          {imports.map((imp) => (
            <div key={imp.id} className="bg-white border border-stone-200 rounded-lg p-3 flex items-center gap-3"
              data-testid={`import-${imp.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{imp.file_name}</div>
                <div className="text-xs text-stone-500">
                  {new Date(imp.created_at).toLocaleString("it-IT")} ·
                  {" "}{imp.righe_lette} righe · {imp.match_ok} match · {imp.polizze_marcate_unpaid} insolute
                </div>
              </div>
              {imp.rolled_back_at ? (
                <span className="text-xs px-2 py-1 bg-stone-200 text-stone-700 rounded-full">Annullato</span>
              ) : (
                <button onClick={() => rollback(imp.id)} className="text-xs text-rose-600 hover:underline">
                  <RefreshCw className="w-3 h-3 inline mr-1" />Rollback
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
